import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const plans = [
  {
    id: "essentiel",
    name: "Essentiel",
    tagline: "Idéal pour établir une présence en ligne ultra-rapide et crédible.",
    biweekly: 50,
    annual: 800,
    annualCompare: 1300,
    features: [
      "Création complète du site web orienté conversion",
      "Hébergement cloud rapide et sécurisé 100% GRATUIT",
      "Nom de domaine personnalisé et certificat SSL inclus",
      "Maintenance technique, sécurité et sauvegardes automatiques",
      "Modifications de contenu disponibles sur devis ponctuel",
    ],
    cta: "Choisir Essentiel",
    featured: false,
  },
  {
    id: "croissance",
    name: "Croissance & Sérénité",
    tagline: "Pour les entreprises actives qui veulent un site toujours à jour.",
    biweekly: 75,
    annual: 1200,
    annualCompare: 1950,
    badge: "Recommandé — Le plus populaire",
    features: [
      "Tout ce qui est inclus dans le forfait Essentiel",
      "Hébergement cloud haute performance 100% GRATUIT",
      "Jusqu'à 3 modifications de contenu par mois (textes, visuels, bannières, offres)",
      "Support prioritaire en moins de 24h ouvrables",
    ],
    cta: "Démarrer avec Croissance",
    featured: true,
  },
];

function Price({ plan, annual }) {
  return (
    <div className="mt-6 flex items-end gap-3">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={annual ? "annual" : "biweekly"}
          initial={{ y: 18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -18, opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="font-display text-5xl font-extrabold tracking-tight text-white"
          data-testid={`price-value-${plan.id}`}
        >
          {annual ? `${plan.annual} $` : `${plan.biweekly} $`}
        </motion.span>
      </AnimatePresence>
      <div className="pb-1.5">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={annual ? "annual" : "biweekly"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="block text-sm text-gray-400"
          >
            {annual ? "/ an" : "/ 2 semaines"}
          </motion.span>
        </AnimatePresence>
        {annual && (
          <span className="block text-xs text-gray-500 line-through">
            {plan.annualCompare} $ / an
          </span>
        )}
      </div>
    </div>
  );
}

export default function Pricing({ onSelectPlan }) {
  const [annual, setAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const choose = async (plan) => {
    onSelectPlan(plan.name);
    setLoadingPlan(plan.id);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: `${plan.id}_${annual ? "annual" : "biweekly"}`,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Impossible d'ouvrir le paiement sécurisé. Réessayez dans un instant.");
      setLoadingPlan(null);
    }
  };

  return (
    <section id="tarifs" className="relative py-20 lg:py-32" data-testid="pricing-section">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <p className="font-mono-label mb-4">Chapitre 02 — Tarifs</p>
          <h2 className="text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">
            Des forfaits simples, transparents{" "}
            <span className="text-emerald-400">et sans surprise</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex justify-center">
          <div
            className="glass relative flex rounded-full p-1.5"
            data-testid="pricing-toggle"
            role="tablist"
            aria-label="Mode de paiement"
          >
            <button
              data-testid="pricing-toggle-biweekly"
              role="tab"
              aria-selected={!annual}
              onClick={() => setAnnual(false)}
              className={`relative z-10 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                !annual ? "text-emerald-950" : "text-gray-400 hover:text-white"
              }`}
            >
              {!annual && (
                <motion.span
                  layoutId="toggle-pill"
                  className="absolute inset-0 -z-10 rounded-full bg-emerald-400"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              Paiement aux 2 semaines
            </button>
            <button
              data-testid="pricing-toggle-annual"
              role="tab"
              aria-selected={annual}
              onClick={() => setAnnual(true)}
              className={`relative z-10 flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                annual ? "text-emerald-950" : "text-gray-400 hover:text-white"
              }`}
            >
              {annual && (
                <motion.span
                  layoutId="toggle-pill"
                  className="absolute inset-0 -z-10 rounded-full bg-emerald-400"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              Paiement annuel
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                  annual
                    ? "bg-emerald-950/20 text-emerald-950"
                    : "border border-emerald-400/30 bg-emerald-500/15 text-emerald-300"
                }`}
                data-testid="pricing-save-badge"
              >
                Économisez 33%
              </span>
            </button>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:gap-10">
          {plans.map((plan, i) => (
            <Reveal key={plan.id} delay={0.15 + i * 0.12}>
              <div className={`relative ${plan.featured ? "rounded-[19px] p-[1.5px]" : ""}`}>
                {plan.featured && (
                  <div className="tracing-beam absolute inset-0 rounded-[19px]" aria-hidden="true" />
                )}
                <article
                  className={`relative flex h-full flex-col rounded-2xl p-8 lg:p-10 ${
                    plan.featured
                      ? "rounded-[18px] bg-[#0D141D] emerald-glow"
                      : "glass transition-all duration-300 hover:-translate-y-1 hover:border-emerald-400/25"
                  }`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.badge && (
                    <span
                      className="absolute -top-3.5 left-8 rounded-full bg-emerald-400 px-4 py-1 text-xs font-bold uppercase tracking-wide text-emerald-950"
                      data-testid="plan-badge-populaire"
                    >
                      {plan.badge}
                    </span>
                  )}
                  <h3 className="text-xl font-semibold text-white sm:text-2xl">{plan.name}</h3>
                  <p className="mt-2 text-sm text-gray-400">{plan.tagline}</p>

                  <Price plan={plan} annual={annual} />
                  {annual && (
                    <p className="mt-2 text-xs font-medium text-emerald-400">
                      4 mois offerts — vous ne payez que 8 mois sur 12
                    </p>
                  )}

                  <ul className="mt-8 flex-1 space-y-3.5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-gray-300">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                          <Check className="h-3 w-3 text-emerald-400" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`plan-cta-${plan.id}`}
                    onClick={() => choose(plan)}
                    disabled={loadingPlan !== null}
                    className={`group mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-all duration-300 disabled:opacity-60 ${
                      plan.featured
                        ? "bg-emerald-500 text-emerald-950 shadow-[0_8px_32px_rgba(16,185,129,0.35)] hover:bg-emerald-400"
                        : "border border-white/15 text-white hover:border-emerald-400/50 hover:bg-emerald-500/10"
                    }`}
                  >
                    {loadingPlan === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {plan.cta}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                  <p className="mt-4 text-center text-xs text-gray-500">
                    Paiement sécurisé par Stripe. Annulable en tout temps.
                  </p>
                </article>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
